import type {
  AssetCategory,
  AssetDomain,
  FinancialItem,
  FinancialItemType,
  FinancialRating,
} from '../types';

const ASSET_CATEGORIES: AssetCategory[] = [
  'real_estate',
  'cash',
  'securities',
  'private_equity',
  'collectibles',
  'vehicle',
  'business_equity',
  'other_asset',
];

const LIABILITY_CATEGORIES: AssetCategory[] = [
  'mortgage',
  'loan',
  'credit_line',
  'tax',
  'other_liability',
];

const ALL_CATEGORIES: AssetCategory[] = [...ASSET_CATEGORIES, ...LIABILITY_CATEGORIES];

const ASSET_CATEGORY_SET = new Set<AssetCategory>(ASSET_CATEGORIES);
const LIABILITY_CATEGORY_SET = new Set<AssetCategory>(LIABILITY_CATEGORIES);

const RATINGS: FinancialRating[] = ['excellent', 'good', 'neutral', 'bad', 'critical'];

export function isAssetCategory(value: unknown): value is AssetCategory {
  return typeof value === 'string' && ALL_CATEGORIES.includes(value as AssetCategory);
}

export function isAssetDomain(value: unknown): value is AssetDomain {
  return value === 'personal' || value === 'business';
}

function isFinancialItemType(value: unknown): value is FinancialItemType {
  return value === 'asset' || value === 'liability';
}

function isFinancialRating(value: unknown): value is FinancialRating {
  return typeof value === 'string' && RATINGS.includes(value as FinancialRating);
}

function normalizeText(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function defaultCategoryForType(type: FinancialItemType): AssetCategory {
  return type === 'liability' ? 'other_liability' : 'other_asset';
}

function categoryForType(type: FinancialItemType, category: AssetCategory): AssetCategory {
  if (type === 'asset' && ASSET_CATEGORY_SET.has(category)) return category;
  if (type === 'liability' && LIABILITY_CATEGORY_SET.has(category)) return category;
  return defaultCategoryForType(type);
}

/** Map legacy free-text category labels to structured enums. */
export function legacyCategoryToAssetCategory(
  type: FinancialItemType,
  rawCategory: string
): AssetCategory {
  const text = normalizeText(rawCategory);
  if (!text) return defaultCategoryForType(type);

  if (type === 'asset') {
    if (/(real estate|property|home equity|house|apartment|condo|land|reit)/.test(text)) {
      return 'real_estate';
    }
    if (/(^cash$|liquid|savings|checking|money market)/.test(text)) return 'cash';
    if (/(stock|bond|etf|mutual fund|brokerage|securities|equity market)/.test(text)) {
      return 'securities';
    }
    if (/(private equity|venture fund|pe fund|vc fund)/.test(text)) return 'private_equity';
    if (/(art|wine|watch|jewelry|collectible)/.test(text)) return 'collectibles';
    if (/(car|vehicle|auto|boat|motorcycle)/.test(text)) return 'vehicle';
    if (/(business equity|company stake|ownership stake)/.test(text)) return 'business_equity';
    return 'other_asset';
  }

  if (/(mortgage|home loan)/.test(text)) return 'mortgage';
  if (/(credit line|line of credit|loc|overdraft|revolving)/.test(text)) return 'credit_line';
  if (/(^tax$|taxes|vat|income tax)/.test(text)) return 'tax';
  if (/(loan|debt|borrow)/.test(text)) return 'loan';
  return 'other_liability';
}

function normalizeCategory(type: FinancialItemType, raw: unknown): AssetCategory {
  if (isAssetCategory(raw)) return categoryForType(type, raw);
  if (typeof raw === 'string') return legacyCategoryToAssetCategory(type, raw);
  return defaultCategoryForType(type);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function optionalFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Normalize Firestore / legacy financial items for in-app state and persistence. */
export function normalizeFinancialItem(raw: FinancialItem): FinancialItem {
  const type = isFinancialItemType(raw.type) ? raw.type : 'asset';
  const domain: AssetDomain = isAssetDomain(raw.domain) ? raw.domain : 'personal';
  const category = normalizeCategory(type, raw.category);
  const rating = isFinancialRating(raw.rating) ? raw.rating : 'neutral';
  const interestRate = optionalFiniteNumber(raw.interestRate);
  const appreciationRate = optionalFiniteNumber(raw.appreciationRate);
  const minimumPayment = optionalFiniteNumber(raw.minimumPayment);
  const linkedAssetId = optionalString(raw.linkedAssetId);
  const acquisitionDate = optionalString(raw.acquisitionDate);
  const notes = optionalString(raw.notes);

  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    name: typeof raw.name === 'string' ? raw.name : '',
    type,
    domain,
    category,
    currentValue:
      typeof raw.currentValue === 'number' && Number.isFinite(raw.currentValue)
        ? raw.currentValue
        : 0,
    monthlyChange:
      typeof raw.monthlyChange === 'number' && Number.isFinite(raw.monthlyChange)
        ? raw.monthlyChange
        : 0,
    interestRate,
    rating,
    acquisitionDate,
    notes,
    linkedAssetId,
    appreciationRate,
    minimumPayment,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
  };
}
